import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import useMouse from "../../utils/hooks/useMouse";
import { TerraLink } from "../TerraLink/TerraLink";
import { Icon } from "@/components/formatters/MXPIcon";
import { setPromotionBannerOpen } from "@/lib/promotionBannerOpenStore";
import { PromoCard } from "./PromoCard";
import { DARK_GRAY_CTA_OVERRIDE, PRIMARY_CTA_BTN, SECONDARY_CTA_BTN } from "./promotionBannerStyles";
interface PromotionBannerProps {
  value?: PromotionBannerValue;
}
export interface PromotionBannerValue {
  header?: any;
  details?: any;
  /** Raw Contentful rich text JSON; rendered when present (avoids passing React tree into personalization payload). */
  detailsJson?: any;
  handleClose?: any;
  hasAcceptedOneTrust?: boolean;
  hasDismissedBanner?: boolean;
  contentfulId?: string;
  cta?: TerraLink;
  LinkComponent?: React.Component<any, any>;
  invisibleBaseline?: boolean;
  theme?: "Yellow" | "Dark Gray";
  displayVariant?: "Banner" | "Promo Card";
  /** Seconds (0–12) to wait before rendering the banner or promo card. */
  displayDelay?: number;
  teaser?: string | null;
  cardImage?: {
    url: string;
    width?: number;
    height?: number;
    altText: string;
    focusArea?: string;
  } | null;
  promoCode?: string | null;
  promoCodeLabel?: string | null;
  dismissLabel?: string | null;
  /** Raw Contentful rich text JSON for the terms & conditions copy. */
  finePrintJson?: any;
}
const CLOSE_BTN = ["rounded-[50px] p-[10px] cursor-pointer bg-white border-0 h-[34px] w-[34px] flex items-center justify-center", "shadow-[0_-1px_5px_rgba(0,0,0,0.1)] text-gray-m10", "[&_.t2-icon]:h-[14px] [&_.t2-icon]:pointer-events-none [&_.t2-icon]:text-gray-m10 [&_.t2-icon_svg_path]:text-gray-m10 [&_.t2-icon_svg_path]:fill-[#252a2e]", "lg:absolute lg:right-6"].join(" ");

/** Matches Tailwind's `max-sm` band, where the Promo Card docks full-width to the bottom. */
const PROMO_CARD_DOCKED_QUERY = "(max-width: 639px)";
const subscribeToPromoCardDocked = (onChange: () => void) => {
  const mq = window.matchMedia(PROMO_CARD_DOCKED_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
};
const usePromoCardDocked = () => {
  return useSyncExternalStore(subscribeToPromoCardDocked, _temp, _temp2);
};
export const PromotionBanner = props => {
  const $ = _c(51);
  const [isClosed, setIsClosed] = useState(true);
  const {
    mouse
  } = useMouse();
  const {
    value
  } = props;
  let t0;
  if ($[0] !== value?.hasAcceptedOneTrust || $[1] !== value?.hasDismissedBanner) {
    t0 = () => {
      if (value?.hasAcceptedOneTrust === true && value?.hasDismissedBanner === true) {
        setIsClosed(true);
      } else {
        if (value?.hasAcceptedOneTrust === true && value?.hasDismissedBanner === false) {
          setIsClosed(false);
        } else {
          if (value?.hasAcceptedOneTrust === true) {
            setIsClosed(true);
          }
        }
      }
    };
    $[0] = value?.hasAcceptedOneTrust;
    $[1] = value?.hasDismissedBanner;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const t1 = value?.hasAcceptedOneTrust;
  const t2 = value?.hasDismissedBanner;
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = [t1, t2];
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t0, t3);
  const delaySeconds = Math.min(Math.max(value?.displayDelay ?? 0, 0), 12);
  const [delayElapsed, setDelayElapsed] = useState(delaySeconds === 0);
  let t4;
  if ($[6] !== delaySeconds) {
    t4 = () => {
      if (delaySeconds === 0) {
        setDelayElapsed(true);
        return;
      }
      setDelayElapsed(false);
      const timer = setTimeout(() => setDelayElapsed(true), delaySeconds * 1000);
      return () => clearTimeout(timer);
    };
    $[6] = delaySeconds;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = value?.contentfulId;
  let t6;
  if ($[8] !== delaySeconds || $[9] !== t5) {
    t6 = [delaySeconds, t5];
    $[8] = delaySeconds;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  useEffect(t4, t6);
  const bannerRef = useRef(null);
  const isPromoCard = value?.displayVariant === "Promo Card";
  const isPromoCardDocked = usePromoCardDocked();
  const isBannerOpen = delayElapsed && value?.hasDismissedBanner !== true && value?.invisibleBaseline !== true && (!isPromoCard || isPromoCardDocked);
  let t7;
  let t8;
  if ($[11] !== isBannerOpen || $[12] !== isPromoCard) {
    t7 = () => {
      setPromotionBannerOpen(isBannerOpen, !isPromoCard);
      const banner = bannerRef.current;
      if (!isBannerOpen || !banner) {
        return;
      }
      const root = document.documentElement;
      const observer = new ResizeObserver(t9 => {
        const [entry] = t9;
        root.style.setProperty("--promotion-banner-height", `${entry.borderBoxSize?.[0]?.blockSize ?? banner.offsetHeight}px`);
      });
      observer.observe(banner);
      return () => {
        observer.disconnect();
        root.style.removeProperty("--promotion-banner-height");
      };
    };
    t8 = [isBannerOpen, isPromoCard];
    $[11] = isBannerOpen;
    $[12] = isPromoCard;
    $[13] = t7;
    $[14] = t8;
  } else {
    t7 = $[13];
    t8 = $[14];
  }
  useEffect(t7, t8);
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  useEffect(_temp4, t9);
  if (!delayElapsed) {
    return null;
  }
  if (value?.hasDismissedBanner === true) {
    return null;
  }
  if (value?.invisibleBaseline === true) {
    return null;
  }
  if (!value?.header && !value?.cta && !value?.details && !value?.detailsJson) {
    return null;
  }
  if (isPromoCard) {
    let t10;
    if ($[16] !== value) {
      t10 = <PromoCard value={value} containerRef={bannerRef} />;
      $[16] = value;
      $[17] = t10;
    } else {
      t10 = $[17];
    }
    return t10;
  }
  let t10;
  if ($[18] !== mouse || $[19] !== value) {
    t10 = () => <button className={`t2-promotion-banner__button t2-promotion-banner__button--close ${CLOSE_BTN}${mouse ? "" : " tabFocus"}`} aria-label="dismiss notice" onClick={e => {
      setIsClosed(true);
      value?.handleClose(e);
    }}><Icon type="t2u-close" className={mouse ? "" : "tabFocus"} /></button>;
    $[18] = mouse;
    $[19] = value;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const CloseButton = t10;
  const t11 = `t2-promotion-banner t2-promotion-banner__container fixed left-0 right-0 bottom-0 w-full z-[50] ${isClosed === false ? "t2-promotion-banner__container--open" : ""}`;
  const t12 = value?.contentfulId;
  const t13 = `text-gray-m10 relative ${value?.theme === "Dark Gray" ? "bg-gray-m9 text-white" : "bg-yellow-m10"}`;
  let t14;
  if ($[21] !== value?.header) {
    t14 = value?.header && <div className="font-bold text-lg leading-[1.3] mb-[10px]">{value?.header}</div>;
    $[21] = value?.header;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== value) {
    t15 = (value?.details || value?.detailsJson) && <div className="text-base leading-[1.3] max-sm:hidden">{value?.detailsJson ? documentToReactComponents(value.detailsJson) : value?.details}</div>;
    $[23] = value;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t14 || $[26] !== t15) {
    t16 = <div className="min-sm:pr-10 min-sm:[&_.richtext-container_p:last-child]:mb-0">{t14}{t15}</div>;
    $[25] = t14;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== value?.cta || $[29] !== value?.theme) {
    t17 = value?.cta && <TerraLink primary={value?.cta?.primary ?? false} destination={value?.cta?.destination} label={value?.cta?.label} excludeDefaultClasses={true} className={`${value?.cta?.primary ? PRIMARY_CTA_BTN : SECONDARY_CTA_BTN} ${value?.theme === "Dark Gray" ? DARK_GRAY_CTA_OVERRIDE : ""}`} />;
    $[28] = value?.cta;
    $[29] = value?.theme;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== CloseButton) {
    t18 = <div className="max-md:hidden flex items-center"><CloseButton /></div>;
    $[31] = CloseButton;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t17 || $[34] !== t18) {
    t19 = <div className="flex justify-between items-center gap-2 lg:mr-10 xl:mr-0 sm:mt-3 md:mt-0">{t17}{t18}</div>;
    $[33] = t17;
    $[34] = t18;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  if ($[36] !== t16 || $[37] !== t19) {
    t20 = <div className="md:flex w-full md:gap-4 md:justify-between">{t16}{t19}</div>;
    $[36] = t16;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== CloseButton) {
    t21 = <div className="flex items-center md:hidden pr-1"><CloseButton /></div>;
    $[39] = CloseButton;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] !== t20 || $[42] !== t21) {
    t22 = <div className="mx-auto py-7 px-5"><div className="max-w-[1060px] mx-auto min-sm:flex min-sm:justify-between min-sm:items-center max-sm:flex max-sm:flex-row max-sm:gap-4 max-sm:justify-between w-full">{t20}{t21}</div></div>;
    $[41] = t20;
    $[42] = t21;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== t13 || $[45] !== t22) {
    t23 = <div className={t13}>{t22}</div>;
    $[44] = t13;
    $[45] = t22;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t11 || $[48] !== t12 || $[49] !== t23) {
    t24 = <div ref={bannerRef} className={t11} key={t12}>{t23}</div>;
    $[47] = t11;
    $[48] = t12;
    $[49] = t23;
    $[50] = t24;
  } else {
    t24 = $[50];
  }
  return t24;
};
function _temp() {
  return window.matchMedia(PROMO_CARD_DOCKED_QUERY).matches;
}
function _temp2() {
  return false;
}
function _temp3() {
  return setPromotionBannerOpen(false);
}
function _temp4() {
  return _temp3;
}